import {CompanyEntity, SiteEntity} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Modal, ModalProps} from "@mantine/core";
import {useCallback} from "react";
import {useOnSiteTimeReportForm} from "./useOnSiteTimeReportForm";
import { DateInput } from "../../date/DateInput/DateInput";
import { OperatorCompanySelect } from "../../select/entity/CompanySelect/OperatorCompanySelect";
import { CompanySelect } from "../../select/entity/CompanySelect/CompanySelect";
import { SiteSelect } from "../../select/entity/SiteSelect/SiteSelect";

export interface OnSiteTimeReportFormProps {
  options?: OnSiteTimeReportFormOptions;
  defaultValues?: OnSiteTimeReportFormDefaultValues;
  onSubmit?: (values: OnSiteTimeReportFormValues) => void;
  submitting?: boolean;
}

export interface OnSiteTimeReportFormOptions {
  regionId?: string
}

export interface OnSiteTimeReportFormValues {
  date: Date
  operator: CompanyEntity | string
  site?: SiteEntity | string
  siteCompany?: CompanyEntity | string
}

export interface OnSiteTimeReportFormDefaultValues extends Partial<OnSiteTimeReportFormValues> {

}

export interface OnSiteTimeReportFormRawValues {
  date?: Date
  operator?: string
  site?: string
  siteCompany?: string;
}

export type OnSiteTimeReportFormType = UseFormReturnType<OnSiteTimeReportFormRawValues, (values: OnSiteTimeReportFormRawValues) => OnSiteTimeReportFormRawValues>

export const OnSiteTimeReportForm = (props: OnSiteTimeReportFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useOnSiteTimeReportForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        date: values.date!,
        operator: values.operator!,
        site: values.site,
        siteCompany: values.siteCompany,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <DateInput
        valueFormat="MM/DD/YYYY HH:mm"
        label={t("common.report.onsitetime.form.field.date", "To Date")}
        placeholder={t<string>("common.report.onsitetime.form.field.date", "To Date")}
        popoverProps={{
          withinPortal: true
        }}
        clearable
        withAsterisk
        required
        {...form.getInputProps("date")} />

      <OperatorCompanySelect
        label={t("common.report.onsitetime.form.field.operator", "Operator")}
        placeholder={t<string>("common.report.onsitetime.form.field.operator.placeholder", "Operator")}
        selector={{
          regionId: options?.regionId,
        }}
        clearable
        withAsterisk
        required
        {...form.getInputProps("operator")} />

      <SiteSelect
        label={t("common.report.onsitetime.form.field.site", "Site")}
        placeholder={t<string>("common.report.onsitetime.form.field.site.placeholder", "Site")}
        selector={{
          regionId: options?.regionId,
        }}
        clearable
        {...form.getInputProps("site")} />

      <CompanySelect
        label={t("common.report.onsitetime.form.field.sitecompany", "Site Company")}
        placeholder={t<string>("common.report.onsitetime.form.field.sitecompany.placeholder", "Site Company")}
        selector={{
          regionId: options?.regionId,
        }}
        clearable
        {...form.getInputProps("siteCompany")} />
      <Divider my="sm" variant="dotted"/>
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface OnSiteTimeReportFormModalProps extends OnSiteTimeReportFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const OnSiteTimeReportFormModal = (props: OnSiteTimeReportFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <OnSiteTimeReportForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
